import * as React from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { Bell as BellIcon, ListIcon, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CommandPaletteTrigger } from '@/components/ui/workspace/command-palette';
import { useAuth } from '@/lib/auth/store';
import { useEngagementQuery } from '@/lib/api/engagements';
import { useNotificationsInboxQuery } from '@/lib/api/notifications';
import { avatarTone, deriveInitials } from '@/components/clients/team-avatars';
import { cn } from '@/lib/utils/cn';

/**
 * Topbar (C20-129 / E10 fix).
 *
 * Mirrors `design_handoff_caliber_v2/prototype/shell.js → renderTopbar()`
 * and the `.topbar / .breadcrumb / .search / .kbd / .chip-snapshot` rules
 * in `prototype/tokens.css`. Replaces the T-0096 implementation which
 * showed an 8-char-UUID stem for the engagement crumb + a plain "My
 * notifications" text link instead of the bell-with-dot affordance.
 *
 * Surface (left → right):
 *   1. Breadcrumb chain
 *      - Firm (`Avero Advisors`) → ClientName → EngagementName → ModuleLabel
 *        ( → SubLabel? ). The client crumb opens the client, the engagement
 *        crumb its overview (C2-46: the engagement was missing, so a reader
 *        deep in a module couldn't see which engagement they were in).
 *      - Both names land via `useEngagementQuery(:id)`; until it answers the
 *        crumbs say "Engagement" rather than show an id.
 *   2. Snapshot chip (conditional)
 *      - Renders `.chip-snapshot` when the route is `/engagements/:id/
 *        snapshot/...`. The actual snapshot-banner (T-0095) still
 *        mounts globally above the chrome; this chip is the inline
 *        breadcrumb-row indicator the prototype shows.
 *   3. Command palette trigger with the ⌘K hint (T-0141). Opens the
 *      shared palette mounted in `routes/__root.tsx`.
 *   4. Density toggle — flips `[data-density]` on `<html>` between
 *      `comfortable` and `compact`; persisted to `localStorage` so a
 *      reload remembers the user's preference.
 *   5. Notification bell — absolutely-positioned red dot when there are
 *      unread items. Click navigates to `/settings/my-notifications`
 *      (matches the current routing); the inbox-level surface still
 *      lives at `/notifications` and is reachable via the sidebar.
 *   6. User avatar — same palette tone as the sidebar user card
 *      (deterministic from id + name).
 *   7. Sign-out button — preserved from the previous topbar.
 */

const FIRM_NAME = 'Avero Advisors';
const DENSITY_STORAGE_KEY = 'caliber.density';

type Density = 'comfortable' | 'compact';

function readInitialDensity(): Density {
  if (typeof window === 'undefined') return 'comfortable';
  const raw = window.localStorage.getItem(DENSITY_STORAGE_KEY);
  return raw === 'compact' ? 'compact' : 'comfortable';
}

/**
 * Density store kept inline. Toggling sets the document attribute the
 * prototype + globals.css already react to (`[data-density="compact"]`)
 * AND persists across reloads via localStorage. Bringing it into the
 * topbar avoids a Zustand store for a single toggle.
 */
function useGlobalDensity(): [Density, () => void] {
  const [density, setDensity] = React.useState<Density>(readInitialDensity);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-density', density);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
    }
  }, [density]);

  const toggle = React.useCallback(() => {
    setDensity((prev) => (prev === 'comfortable' ? 'compact' : 'comfortable'));
  }, []);

  return [density, toggle];
}

export function Topbar() {
  const identity = useAuth((s) => s.identity);
  const clear = useAuth((s) => s.clear);
  const { pathname } = useLocation();
  const [density, toggleDensity] = useGlobalDensity();

  const engagementId = React.useMemo(() => {
    const m = pathname.match(/^\/engagements\/([0-9a-f-]{36})(?:\/|$)/i);
    return m?.[1] ?? null;
  }, [pathname]);

  const engagementQuery = useEngagementQuery(engagementId ?? undefined);
  const notificationsQuery = useNotificationsInboxQuery();

  const unreadCount = notificationsQuery.data?.unread_count ?? 0;

  const inSnapshot = engagementId !== null && /\/snapshot(\/|$)/.test(pathname);

  return (
    <header
      // .topbar — h-14 (56px), paper-0 bg, ink-100 border-bottom, sticky,
      // padding 0 24px, gap 16px, z above content.
      className="sticky top-0 z-10 flex h-14 items-center gap-4 border-b border-ink-100 bg-paper-0 px-6"
    >
      <Breadcrumb
        pathname={pathname}
        engagementId={engagementId}
        engagementName={engagementQuery.data?.name ?? null}
        clientId={engagementQuery.data?.client_id ?? null}
        clientName={engagementQuery.data?.client_name ?? null}
      />
      {inSnapshot ? (
        <span className="chip-snapshot ml-2" aria-label="Viewing snapshot">
          Snapshot view
        </span>
      ) : null}

      <div className="ml-auto flex items-center gap-2">
        {/* Command palette trigger (T-0141) — replaces the disabled
            search input that held this slot. Same `.search` footprint;
            opens the palette, as does Cmd/Ctrl+K. The commands come from
            whichever screen is mounted plus the app-wide ones registered
            in `app-shell.tsx`. */}
        {/* Shown from lg, narrower below xl, so the breadcrumb keeps room at
            tablet width (C2-35); Cmd/Ctrl+K opens the palette at any width. */}
        <CommandPaletteTrigger className="hidden w-[180px] lg:inline-flex xl:w-[280px]" />

        {/* Density toggle — flips `[data-density]` on the html element. */}
        <button
          type="button"
          onClick={toggleDensity}
          title="Toggle density"
          aria-label="Toggle density"
          aria-pressed={density === 'compact'}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-card px-2 text-meta font-medium text-ink-700',
            'hover:bg-paper-100 hover:text-ink-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
          )}
        >
          <ListIcon className="h-3.5 w-3.5 text-ink-500" aria-hidden="true" />
          <span className="hidden xl:inline">
            {density === 'comfortable' ? 'Comfortable' : 'Compact'}
          </span>
        </button>

        {/* Notification bell with absolute-positioned unread dot. */}
        <Link
          to="/settings/my-notifications"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          className={cn(
            'relative inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-700',
            'hover:bg-paper-100 hover:text-ink-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
          )}
        >
          <BellIcon className="h-4 w-4 text-ink-500" aria-hidden="true" />
          {unreadCount > 0 ? (
            <span
              aria-hidden="true"
              className="absolute right-1 top-1 block h-1.5 w-1.5 rounded-full bg-posture-critical"
            />
          ) : null}
        </Link>

        {identity ? (
          <>
            <UserAvatar
              userId={identity.userId}
              displayName={identity.displayName || identity.email || 'Dev Admin'}
            />
            <Button
              size="sm"
              variant="ghost"
              aria-label="Sign out"
              title="Sign out"
              onClick={() => {
                clear();
                window.location.href = '/login';
              }}
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span className="hidden lg:inline">Sign out</span>
            </Button>
          </>
        ) : (
          <Link to="/login" className="text-body-sm text-ink-500 hover:underline">
            Sign in
          </Link>
        )}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

interface BreadcrumbProps {
  pathname: string;
  engagementId: string | null;
  engagementName: string | null;
  clientId: string | null;
  clientName: string | null;
}

const CRUMB_LINK =
  'truncate text-ink-500 no-underline hover:text-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

function Separator() {
  return (
    <span aria-hidden="true" className="shrink-0 text-ink-300">
      /
    </span>
  );
}

export function Breadcrumb({
  pathname,
  engagementId,
  engagementName,
  clientId,
  clientName,
}: BreadcrumbProps) {
  const section = sectionForPath(pathname);

  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-body-sm">
      <Link to="/" className={cn(CRUMB_LINK, 'shrink-0')}>
        {FIRM_NAME}
      </Link>
      {engagementId ? (
        <>
          {/* The client crumb gives way first: below xl the engagement and
              the module are what the reader needs to see. */}
          {clientName ? (
            <span className="hidden min-w-0 items-center gap-2 xl:flex">
              <Separator />
              {clientId ? (
                <Link
                  to="/clients/$clientId"
                  params={{ clientId }}
                  className={cn(CRUMB_LINK, 'max-w-[180px]')}
                  title={clientName}
                >
                  {clientName}
                </Link>
              ) : (
                <span className="max-w-[180px] truncate text-ink-500" title={clientName}>
                  {clientName}
                </span>
              )}
            </span>
          ) : null}
          <Separator />
          <Link
            to="/engagements/$engagementId"
            params={{ engagementId }}
            className={cn(CRUMB_LINK, 'max-w-[260px]')}
            title={engagementName ?? undefined}
          >
            {engagementName ?? 'Engagement'}
          </Link>
          {section.engagementModule ? (
            <>
              <Separator />
              {section.engagementSub && section.moduleBase ? (
                // The module's base path is dynamic (contract/baseline,
                // health, golive); we can't statically type it against
                // TanStack's `to` so we drop to a plain `<a href>`.
                // Client-side routing still applies because the router
                // intercepts same-origin anchor clicks.
                <a
                  href={`/engagements/${engagementId}/${section.moduleBase}`}
                  className={cn(CRUMB_LINK, 'shrink-0')}
                >
                  {section.engagementModule}
                </a>
              ) : (
                <span className="shrink-0 font-medium text-ink-900">
                  {section.engagementModule}
                </span>
              )}
              {section.engagementSub ? (
                <>
                  <Separator />
                  <span className="shrink-0 font-medium text-ink-900">{section.engagementSub}</span>
                </>
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        <>
          <Separator />
          <span className="truncate font-medium text-ink-900">{section.top}</span>
          {section.settingsSub ? (
            <>
              <Separator />
              <span className="truncate font-medium text-ink-900">{section.settingsSub}</span>
            </>
          ) : null}
        </>
      )}
    </nav>
  );
}

interface SectionLabels {
  /** Top-level section label when not inside an engagement. */
  top: string;
  /** Settings-only secondary crumb (e.g. "Users & roles"). */
  settingsSub: string | null;
  /** Per-engagement module crumb (e.g. "Contract Compliance"). */
  engagementModule: string | null;
  /** Per-engagement sub-module crumb when nested under a module. */
  engagementSub: string | null;
  /** Base segment for the module crumb's link (e.g. "contract" → /contract/baseline). */
  moduleBase: string | null;
}

export function sectionForPath(p: string): SectionLabels {
  const empty: SectionLabels = {
    top: 'Portfolio',
    settingsSub: null,
    engagementModule: null,
    engagementSub: null,
    moduleBase: null,
  };
  if (p === '/' || p === '') return empty;
  if (p.startsWith('/clients')) return { ...empty, top: 'Clients' };
  if (p.startsWith('/notifications')) return { ...empty, top: 'Notifications' };
  if (p.startsWith('/components')) return { ...empty, top: 'Component library' };
  if (p.startsWith('/settings')) {
    return { ...empty, top: 'Firm settings', settingsSub: settingsSub(p) };
  }
  if (p.startsWith('/engagements/')) {
    const labels = engagementCrumbs(p);
    return { ...empty, top: 'Portfolio', ...labels };
  }
  if (p === '/engagements' || p.startsWith('/engagements?') || p.startsWith('/viewer'))
    return empty;
  // Any other address is the not-found page (C2-46); the crumb says so
  // rather than claim the reader is on the portfolio.
  return { ...empty, top: 'Page not found' };
}

const SETTINGS_LABELS: Record<string, string> = {
  users: 'Users & roles',
  viewers: 'External viewers',
  library: 'Methodology library',
  'requirement-library': 'Requirement library',
  'discovery-frameworks': 'Discovery frameworks',
  'vendor-knowledge': 'Vendor knowledge base',
  queue: 'Promote-back queue',
  notifications: 'Notification policies',
  'my-notifications': 'My notifications',
  'ai-costs': 'AI cost governance',
  brand: 'Brand & reports',
  'assessment-defaults': 'Assessment defaults',
};

function settingsSub(p: string): string | null {
  if (p === '/settings' || p === '/settings/') return null;
  const tail = p.replace(/^\/settings\/?/, '');
  if (!tail) return null;
  const first = tail.split('/').filter(Boolean)[0];
  if (!first) return null;
  return SETTINGS_LABELS[first] ?? titleize(first);
}

const ENG_MODULE_LABELS: Record<string, string> = {
  '': 'Overview',
  overview: 'Overview',
  contract: 'Contract Compliance',
  'health-readiness': 'Health & Readiness',
  selection: 'Selection',
  discovery: 'Discovery',
  register: 'Registers',
  rfis: 'RFIs',
  issues: 'Issues',
  documents: 'Documents',
  chat: 'Chat',
  snapshot: 'Snapshots',
  audit: 'Audit trail',
  archive: 'Archive',
  proposals: 'Proposals',
};

const ENG_SUB_LABELS: Record<string, Record<string, string>> = {
  contract: {
    '': 'Baseline',
    baseline: 'Baseline',
    deliverables: 'Deliverables',
    changes: 'Change orders',
    checkpoints: 'IV&V checkpoints',
    vendor: 'Vendor scoring',
    deviations: 'Deviations',
    pulse: 'Pulse reports',
  },
  documents: {
    review: 'AI proposal review',
  },
  discovery: {
    sessions: 'Sessions',
    findings: 'Findings',
    maps: 'Process maps',
    setup: 'Set up',
    signoff: 'Sign-off',
  },
  // T-0242: the tabs; a domain or criterion id after the tab is not a crumb.
  'health-readiness': {
    domains: 'Domains',
    criteria: 'Go-live criteria',
    findings: 'Findings',
    interviews: 'Interview notes',
    report: 'Report',
    snapshots: 'Snapshots & trend',
  },
};

/** Modules whose crumb is the tab only: what follows it is a row id. */
const SUB_IS_FIRST_SEGMENT = new Set(['health-readiness']);

/** The crumb for a module's own index page, where it has tabs (Discovery -> Overview). */
const MODULE_INDEX_SUB: Record<string, string> = {
  discovery: 'Overview',
};

/**
 * Module-key → default-landing-path. Some modules (contract) don't have
 * an `index.tsx` at the bare module path, so the breadcrumb's parent
 * link needs to point at the canonical sub-route. Mirrors the sidebar's
 * `ENGAGEMENT_SUB_ITEMS` Contract item using `contract/baseline`.
 */
const MODULE_DEFAULT_PATH: Record<string, string> = {
  contract: 'contract/baseline',
};

function engagementCrumbs(
  p: string,
): Pick<SectionLabels, 'engagementModule' | 'engagementSub' | 'moduleBase'> {
  const tail = p.replace(/^\/engagements\/[^/]+/, '').replace(/^\//, '');
  if (!tail) {
    return { engagementModule: 'Overview', engagementSub: null, moduleBase: null };
  }
  const parts = tail.split('/').filter(Boolean);
  const moduleKey = parts[0]!;
  const subKey = (SUB_IS_FIRST_SEGMENT.has(moduleKey) ? parts.slice(1, 2) : parts.slice(1)).join(
    '/',
  );
  const moduleLabel = ENG_MODULE_LABELS[moduleKey] ?? titleize(moduleKey);
  const subLabel = subKey
    ? (ENG_SUB_LABELS[moduleKey]?.[subKey] ?? titleize(subKey))
    : (MODULE_INDEX_SUB[moduleKey] ?? null);
  // moduleBase is the path the parent crumb links to. Falls back to the
  // module key itself for modules with an index.tsx route (discovery).
  // Contract has no index — it lands on `contract/baseline`.
  return {
    engagementModule: moduleLabel,
    engagementSub: subLabel,
    moduleBase: subLabel ? (MODULE_DEFAULT_PATH[moduleKey] ?? moduleKey) : null,
  };
}

function titleize(s: string): string {
  return s
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// User avatar — palette tone driven by id+name hash. Matches the sidebar
// user card so a single user reads as the "same" person across surfaces.
// ---------------------------------------------------------------------------

function UserAvatar({ userId, displayName }: { userId: string; displayName: string }) {
  const tone = avatarTone({ id: userId, name: displayName });
  const initials = deriveInitials(displayName);
  return (
    <span
      aria-hidden="true"
      title={displayName}
      className={cn(
        'inline-flex h-7 w-7 flex-none items-center justify-center rounded-full border border-paper-0 font-mono text-[11px] font-semibold',
        `bg-avatar-${tone}-bg`,
        `text-avatar-${tone}-ink`,
      )}
    >
      {initials}
    </span>
  );
}
